import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { I18nService } from '@app/i18n/services';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { Observable, Subject } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { IAppTermDocument } from '.';
import { downloadLink, ObjectString, versionCompare, versionToKey } from '../../../shared/utilities';
import { LegalDisplayDialogComponent } from '../../modules/manager/modules/company/components/legal-display-dialog/legal-display.dialog';
import { ILegalDisplay } from '../../modules/manager/modules/company/components/legal-display-dialog/legal-display.models';
import { AppCluster } from '../appkey';
import { AuthService } from '../auth';
import { ICompany } from '../company';
import { DialogClass, DialogService, IDialogConfig } from '../dialog';
import { Endpoint, EndpointService } from '../endpoints';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '../feedback';
import { ProgressIndicatorService } from '../progress';
import {
	DisplayableContentType,
	IAppRequiredTerms,
	IAppTermShallowDocument,
	ILegalsServiceData,
	ILegalUploader,
	isITerm,
	ISortedAppTerm,
	ISortedTerm,
	ITerm,
	ITermResponse,
	ITermsPayload,
	TermState,
	TermType
} from './legals.models';

@Injectable({
	providedIn: 'root'
})
export class LegalsService extends OnDestroyMixin {
	private dataSubject = new Subject<ILegalsServiceData>();
	public dataObs = this.dataSubject.asObservable();

	public data: ILegalsServiceData;

	constructor(
		private authService: AuthService,
		private i18nService: I18nService,
		private dialogService: DialogService,
		private endpointService: EndpointService,
		private http: HttpClient,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService
	) {
		super();
	}

	public clearCache() {
		this.data = undefined;
	}

	/**
	 * Get badge class associated with each term state.
	 * @param state TermState enum
	 */
	public getStateBadge(state: TermState): string {
		switch (state) {
			case TermState.live:
			case TermState.signed:
				return 'badge-success';
			case TermState.draft:
				return 'badge-warning';
			case TermState.unsigned:
				return 'badge-danger';
			default:
				return 'badge-dark';
		}
	}

	/**
	 * Get label associated with each term state.
	 * @param state TermState enum
	 */
	public getStateLabel(state: TermState): string {
		switch (state) {
			case TermState.live:
				return this.i18nService.get('active', 'app.descriptor.active');
			case TermState.draft:
				return this.i18nService.get('draft', 'app.descriptor.draft');
			case TermState.signed:
				return this.i18nService.get('accepted', 'app.descriptor.accepted-version');
			case TermState.unsigned:
				return this.i18nService.get('unsigned', 'app.descriptor.unsigned-version');
			default:
				return this.i18nService.get('archived', 'app.descriptor.archived');
		}
	}

	/**
	 * Get locale specific term out of a ISortedTerm object. If locale does not exist will return first
	 * available term.
	 *
	 * @param item Sorted and grouped terms
	 * @param locale Term locale to get and return, defaults to either account locale or EN
	 */
	public localeSuitedTerms(item: ISortedTerm, locale = this.authService.accountUser?.locale): ITerm {
		const useLocale = locale ? locale : 'en';
		const versionKey = versionToKey(item.version);
		const terms: ObjectString<ITerm> = item.versions[versionKey];

		return terms[useLocale] ? terms[useLocale] : Object.values(terms)[0];
	}

	/**
	 * Calculates additional meta data for a ISortedAppTerm object and returns the
	 * updated object. This is useful utility values not being provided by backend.
	 *
	 * @param term A ISortedAppTerm object
	 */
	private transformISortedAppTerm(term: ISortedAppTerm): ISortedAppTerm {
		return {
			...term,
			hasDraft: !term?.activeVersion || term.latestVersion > term?.activeVersion
		};
	}

	/**
	 * Adds a single App Term version document into cache and returns the sorted document.
	 *
	 * @param document IAppTermDocument representing a single version of a App Term
	 */
	public addAppTermDocument(document: IAppTermDocument): ISortedAppTerm {
		if (this.data?.app) {
			const index = this.data.app.findIndex((term) => term.termsId === document.termsId);

			if (index > -1) {
				const sortedTerm = this.data.app[index];

				sortedTerm.displayName = document.displayName;
				sortedTerm.alias = document?.alias;

				const activeVersion = sortedTerm?.activeVersion > -1 ? sortedTerm.activeVersion : -1;

				if (document.version > activeVersion && document.status === 'active') {
					sortedTerm['activeVersion'] = document.version;
				}

				sortedTerm.latestVersion = document.version > sortedTerm.latestVersion ? document.version : sortedTerm.latestVersion;

				sortedTerm.documents[document.version] = {
					version: document.version,
					displayVersion: document.displayVersion,
					active: document.active,
					status: document.status,
					url: document?.url,
					filename: document?.filename,
					createdBy: document.createdBy,
					publishedBy: document?.publishedBy
				};

				this.data.app[index] = this.transformISortedAppTerm(sortedTerm);

				return this.data.app[index];
			} else {
				const sortedTerm = this.transformISortedAppTerm({
					companyId: document.companyId,
					termsId: document.termsId,
					entityType: document.entityType,
					displayName: document.displayName,
					alias: document?.alias,
					activeVersion: document.version,
					latestVersion: document.version,
					documents: {
						[document.version]: {
							version: document.version,
							displayVersion: document.displayVersion,
							active: document.active,
							status: document.status,
							url: document?.url,
							filename: document?.filename,
							createdBy: document.createdBy,
							publishedBy: document?.publishedBy
						}
					}
				});

				this.data.app.push(sortedTerm);

				return sortedTerm;
			}
		}
	}

	public getAppTermDocument(term: ISortedAppTerm, version: number | string): IAppTermDocument {
		return {
			companyId: term.companyId,
			termsId: term.termsId,
			entityType: term.entityType,
			displayName: term.displayName,
			alias: term?.alias,
			version: term.documents[version].version,
			displayVersion: term.documents[version].displayVersion,
			active: term.documents[version]?.active,
			status: term.documents[version].status,
			url: term.documents[version]?.url,
			filename: term.documents[version]?.filename,
			createdBy: term.documents[version].createdBy,
			publishedBy: term.documents[version]?.publishedBy
		};
	}

	/**
	 * Converts a list of ITerm objects into a list of ISortedTerm.
	 *
	 * @param terms ITerm[]
	 */
	public sortedTerms(terms: ITerm[]): ISortedTerm[] {
		const sortedTerms = {};

		terms.forEach((term: ITerm) => {
			// Create base object
			if (!sortedTerms[term.id]) {
				sortedTerms[term.id] = {
					id: term.id,
					name: term.name,
					entityId: term?.entityId,
					entityType: term?.entityType,
					versions: {}
				};
			}

			// Set meta data
			const versionHistory = sortedTerms[term.id]?.versionHistory ? sortedTerms[term.id]?.versionHistory : [];
			const version = sortedTerms[term.id]?.version ? sortedTerms[term.id]?.version : term.version;

			if (!versionHistory.includes(version)) {
				versionHistory.push(version);
				versionHistory.sort();
				sortedTerms[term.id]['versionHistory'] = versionHistory;
			}

			if (versionCompare(term.version, version) >= 0) {
				sortedTerms[term.id]['version'] = term.version;
				sortedTerms[term.id]['active'] = term.active;
			}

			// Sort based on version, creates object structure version -> locale -> term
			const highestActiveVersion = sortedTerms[term.id]['highestActiveVersion']
				? sortedTerms[term.id]['highestActiveVersion']
				: term.version;

			if (term.active && versionCompare(term.version, highestActiveVersion) >= 0) {
				sortedTerms[term.id]['highestActiveVersion'] = term.version;
			}

			const highestDraftVersion = sortedTerms[term.id]['highestDraftVersion']
				? sortedTerms[term.id]['highestDraftVersion']
				: term.version;

			if (!term.active && versionCompare(term.version, highestDraftVersion) >= 0) {
				sortedTerms[term.id]['highestDraftVersion'] = term.version;
			}

			const key = versionToKey(term.version);

			if (!sortedTerms[term.id]['versions'][key]) {
				sortedTerms[term.id]['versions'][key] = {
					[term.locale]: term
				};
			} else {
				sortedTerms[term.id]['versions'][key][term.locale] = term;
			}
		});

		return ((Object.values(sortedTerms) as ISortedTerm[]) || []).sort((a: ISortedTerm, b: ISortedTerm) => {
			return a.name.localeCompare(b.name);
		});
	}

	/**
	 * Get App Key term document, returned in base64 string format. Supports getting app terms.
	 *
	 * @param termsId Terms ID
	 * @param version Term version
	 * @param type Term type
	 * @param locale Optional locale to get
	 */
	public getTermData(
		termsId: string,
		version: string | number,
		type: TermType,
		locale?: string
	): Observable<HttpResponse<string>> {
		let url = `${this.endpointService.url(Endpoint.oauthAppTerms)}/sbc/${termsId}/download`;

		url += `
			?version=${version}
			${locale ? `&locale=${locale}` : ''}
		`;

		return new Observable<HttpResponse<string>>((observer) => {
			this.http.get(url, { observe: 'response', responseType: 'text' }).subscribe(
				(result: HttpResponse<string>) => {
					observer.next(result);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	public serveExternalDocument(url: string): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.warning,
			message: this.i18nService.get(
				'This document is not hosted by Zone5, following this link opens a new browser tab and redirects to an external URL. Are you sure you want to continue?',
				'app.prompt.legal.open-external-document'
			),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					window.open(url, '_blank');
				}
			}
		});
	}

	public serveDocument(
		term: ITerm | IAppTermDocument,
		data: HttpResponse<string>,
		isAdmin: boolean,
		type: TermType,
		state: TermState,
		callback: (accepted: boolean) => void,
		cluster?: AppCluster
	) {
		const contentType = data.headers.get('content-type');
		const filename = data.headers.get('content-disposition').split(';')[1].split('=')[1].replace(/\"/g, '');

		switch (contentType) {
			case DisplayableContentType.pdf:
			case DisplayableContentType.html:
			case DisplayableContentType.text:
				this.showLegalDialog(term, contentType, filename, data.body, isAdmin, type, state, callback, cluster);
				break;
			default:
				downloadLink(type, filename, data.body);
				break;
		}
	}

	private showLegalDialog(
		terms: ITerm | IAppTermDocument,
		contentType: string,
		filename: string,
		data: string,
		isAdmin: boolean,
		type: TermType,
		state: TermState,
		callback: (accepted: boolean) => void,
		cluster?: AppCluster
	) {
		const dialogData: ILegalDisplay = {
			terms,
			data: 'data:' + contentType + ';base64, ' + data,
			companyAdmin: isAdmin,
			state,
			type,
			cluster,
			download: () => {
				downloadLink(contentType, filename, data);
			},
			callback: (success: boolean) => {
				const clusterId = cluster ? cluster.clusterId : undefined;

				if (success) {
					this.progressIndicatorService.spinner(true);
				}
			}
		};
		const config: IDialogConfig = {
			data: dialogData,
			height: '100vh',
			panelClass: DialogClass.large
		};
		this.dialogService.open(LegalDisplayDialogComponent, config);
	}

	/**
	 * Handle legal document upload.
	 *
	 * @param legalObject ILegalUploader object
	 * @param clusterId Optional cluster ID, if provided will upload legal document for a specific cluster
	 */
	public addLegalDocument(legalObject: ILegalUploader, type: TermType, clusterId?: string): Observable<ITermResponse> {
		const version = legalObject.version ? legalObject.version : '1.0';

		let options = {};
		let body = null;
		let url = `${this.endpointService.url(Endpoint.oauthAppTerms)}`;

		url += `
			${legalObject.id ? `/${legalObject.id}/version` : ''}
			?name=${encodeURIComponent(legalObject.name)}
			&active=${legalObject.active}
			${legalObject?.version ? `&version=${encodeURIComponent(version)}` : ''}
			${
				legalObject?.displayVersion
					? `&displayVersion=${encodeURIComponent(legalObject.displayVersion)}`
					: `&displayVersion=${encodeURIComponent(legalObject.version)}`
			}
			${legalObject?.locale ? `&locale=${legalObject.locale}` : ''}
			${legalObject?.alias ? `&alias=${encodeURIComponent(legalObject.alias)}` : ''}
		`;

		if (legalObject?.file) {
			body = legalObject.file;
			options = {
				headers: new HttpHeaders({
					'Content-Type': 'application/octet-stream',
					'Upload-Content-Type': legalObject.file.type,
					'Content-Disposition': 'attachment; filename="' + legalObject.file.name + '"'
				})
			};
		} else if (legalObject?.url) {
			url += `&url=${encodeURIComponent(legalObject.url)}`;
		}

		return this.http.post(url, body, options).pipe(
			map((result: ICompany | IAppTermDocument) => {
				if ('termsId' in result) {
					this.addAppTermDocument(result);
				}

				return { [type]: result };
			}),
			first()
		);
	}

	/**
	 * Set active state for App Key terms and conditions.
	 *
	 * @param term The terms to update state for
	 * @param active State to set the terms too
	 * @param clusterId Optional clusterId, if provided will set terms for a cluster
	 */
	public activeState(
		termsId: string,
		version: string | number,
		active: boolean,
		type: TermType,
		locale?: string,
		clusterId?: string
	): Observable<ITermResponse> {
		let url = `${this.endpointService.url(Endpoint.oauthAppTerms)}`;

		url += `
			/${termsId}/activate
			?version=${version}
			&active=${active}
		`;

		if (locale) {
			url += `
				&locale=${locale}
			`;
		}

		return new Observable<ITermResponse>((observer) => {
			this.http.post<ICompany | IAppTermDocument>(url, null).subscribe(
				(result: ICompany | IAppTermDocument) => {
					if ('termsId' in result) {
						const index = this.data.app.findIndex((term) => term.termsId === termsId);

						Object.values(this.data.app[index].documents).forEach((document) => {
							if (result.version !== document.version && document.status === 'active') {
								document.status = 'archived';
							}
						});

						observer.next({ [type]: this.addAppTermDocument(result) });
					} else {
						observer.next({ [type]: result });
					}

					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Unassociate a list of term IDs from a client ID.
	 *
	 * @param clientId Client ID to remove terms from
	 * @param termIds Term IDs to remove from client ID
	 */
	public unassociateTermsFromAppKeys(clientId: string, termIds: string[]): Observable<boolean> {
		const url = `${this.endpointService.url(
			Endpoint.oauthAppTerms
		)}/${clientId}/unassociate-terms?termsId=${termIds.toString()}`;

		return new Observable<boolean>((observer) => {
			this.http.post(url, null).subscribe(
				() => {
					if (this.data?.appKey?.[clientId]) {
						this.data.appKey[clientId].oAuthApp = this.data.appKey[clientId].oAuthApp.filter(
							(term) => termIds.indexOf(term.termsId) === -1
						);
					}

					observer.next(true);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Associates a app term to a list of client IDs.
	 *
	 * @param clientIds A list of client IDs
	 * @param termId App term ID
	 */
	public associateTermToAppKeys(clientIds: string[], termId: string): Observable<boolean> {
		const url = `${this.endpointService.url(
			Endpoint.oauthAppTerms
		)}/${termId}/associate-apps?clientId=${clientIds.toString()}`;

		return new Observable<boolean>((observer) => {
			this.http.post(url, null).subscribe(
				() => {
					// Clear cache to force refresh
					clientIds.forEach((clientId) => {
						delete this.data?.appKey?.[clientId];
					});
					observer.next(true);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Associates a list of app terms to a client ID.
	 *
	 * @param clientId App key client ID
	 * @param termIds A list of app term IDs
	 */
	public associateAppKeyToTerms(
		clientId: string,
		termIds: string[],
		terms: ISortedAppTerm[]
	): Observable<ISortedAppTerm[]> {
		const url = `${this.endpointService.url(
			Endpoint.oauthAppTerms
		)}/${clientId}/associate-terms?termsId=${termIds.toString()}`;

		return new Observable<ISortedAppTerm[]>((observer) => {
			this.http.post<string[]>(url, null).subscribe(
				(res: string[]) => {
					if (!this.data) {
						this.data = {
							appKey: {
								[clientId]: {
									cluster: [],
									oAuthApp: [...terms]
								}
							}
						};
					} else if (!this.data['appKey']) {
						this.data['appKey'] = {
							[clientId]: {
								cluster: [],
								oAuthApp: [...terms]
							}
						};
					} else {
						this.data.appKey[clientId].oAuthApp = [...this.data.appKey[clientId].oAuthApp, ...terms];
					}

					observer.next(this.data.appKey[clientId].oAuthApp);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Update existing App terms. Currently only supports updating name.
	 *
	 * @param termId App term ID
	 * @param name New name of term
	 */
	public updateTerms(termId: string, name: string, alias: string): Observable<ISortedAppTerm[]> {
		const queryString = Object.entries({ name, alias })
			.map(([key, value]) => (value?.length > -1 ? `${key}=${value}` : ''))
			.filter((param) => param.length > 0)
			.join('&');

		const url = `${this.endpointService.url(Endpoint.oauthAppTerms)}/${termId}/update?${queryString}`;

		return this.http.post<IAppTermShallowDocument>(url, null).pipe(
			map((res: IAppTermShallowDocument) => {
				const index = this.data.app.findIndex((term) => term.termsId === termId);

				this.data.app[index] = {
					...this.data.app[index],
					displayName: res?.displayName,
					alias: res?.alias
				};

				return this.data.app;
			}),
			first()
		);
	}

	public localiseTerms(
		termId: string,
		localisation: { displayNames?: { [locale: string]: string } }
	): Observable<{ displayNames: { [locale: string]: string } }> {
		const url = `${this.endpointService.url(Endpoint.oauthAppTerms)}/${termId}/localise`;

		return this.http.post<{ displayNames: { [locale: string]: string } }>(url, localisation).pipe(
			tap({
				next: (res) => {
					const index = this.data.app.findIndex((term) => term.termsId === termId);

					this.data.app[index]['displayNames'] = res.displayNames;
				}
			}),
			first()
		);
	}

	/**
	 * Delete draft App terms
	 *
	 * @param termId App term ID
	 * @param locale Term locale
	 * @param version Term version
	 */
	public deleteDraftTerms(termId: string, version: number | string): Observable<boolean> {
		const url = `${this.endpointService.url(Endpoint.oauthAppTerms)}/${termId}/draft?version=${version}`;

		return new Observable<boolean>((observer) => {
			this.http.delete(url).subscribe(
				() => {
					const index = this.data.app.findIndex((term) => term.termsId === termId);

					delete this.data.app[index].documents[version];

					if (Object.keys(this.data.app[index].documents).length === 0) {
						this.data.app.splice(index, 1);
					} else {
						this.data.app[index].latestVersion = parseInt(
							Object.keys(this.data.app[index].documents).sort()[0],
							10
						);
						this.data.app[index] = this.transformISortedAppTerm(this.data.app[index]);
					}

					observer.next(true);
					observer.complete();
				},
				(error: any) => {
					observer.error(error);
					observer.complete();
				}
			);
		});
	}

	/**
	 * Get list of App terms attached to a client ID.
	 *
	 * @param clientId App key client ID
	 * @param force Force cache refresh, defaults to false.
	 * @param history Get full term history or only latest active terms. Defaults to false.
	 */
	public getClientIdTerms(clientId: string, force = false, history = false): Observable<IAppRequiredTerms> {
		let url = `${this.endpointService.url(Endpoint.oauthAppTerms)}/${clientId}/required`;

		url += `
			${!history ? '?activeOnly=true' : ''}
		`;

		return new Observable<IAppRequiredTerms>((observer) => {
			let refreshCache = force || !this.data?.appKey?.[clientId];

			// If there is a newer version available in cache refresh required terms
			if (this.data?.app && this.data?.appKey?.[clientId]) {
				for (let i = 0; i < this.data.appKey[clientId].oAuthApp.length; i++) {
					const appTerm = this.data.app.find(
						(sortedTerm) => sortedTerm.termsId === this.data.appKey[clientId].oAuthApp[i].termsId
					);

					if (this.data.appKey[clientId].oAuthApp[i].activeVersion < appTerm?.activeVersion) {
						refreshCache = true;
						break;
					}
				}
			}

			if (refreshCache) {
				this.http.get<IAppRequiredTerms>(url).subscribe(
					(result: IAppRequiredTerms) => {
						if (!this.data) {
							this.data = {
								appKey: {}
							};
						} else if (!this.data.appKey) {
							this.data.appKey = {};
						}

						this.data.appKey[clientId] = result;

						observer.next(this.data.appKey[clientId]);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.appKey[clientId]);
				observer.complete();
			}
		});
	}

	public getOathAppLegals(history = true, force = false): Observable<ISortedAppTerm[]> {
		const url = `${this.endpointService.url(Endpoint.oauthAppTerms)}${!history ? '?latestOnly=true' : ''}`;

		return new Observable<ISortedAppTerm[]>((observer) => {
			const refreshCache = force || !this.data || !this.data.hasOwnProperty('app');

			if (refreshCache) {
				this.http.get<ISortedAppTerm[]>(url).subscribe(
					(result: ISortedAppTerm[]) => {
						if (!this.data) {
							this.data = {};
						}

						this.data['app'] = result.map((legal) => this.transformISortedAppTerm(legal));
						this.dataSubject.next(this.data);

						observer.next(this.data.app);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.app);
				observer.complete();
			}
		});
	}
}
